/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `field_description` message.
 *
 * Must be logged before developer field is used
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class FieldDescriptionMesg : Mesg {
    public constructor() : super("FieldDescription", Profile.MesgNum.FIELD_DESCRIPTION)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** developer_data_index. */
    public var developerDataIndex: UByte?
        get() = getFieldValue(DEVELOPER_DATA_INDEX_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEVELOPER_DATA_INDEX_FIELD_NUM, value)

    /** field_definition_number. */
    public var fieldDefinitionNumber: UByte?
        get() = getFieldValue(FIELD_DEFINITION_NUMBER_FIELD_NUM) as? UByte
        set(value) = setFieldValue(FIELD_DEFINITION_NUMBER_FIELD_NUM, value)

    /** fit_base_type_id. */
    public var fitBaseTypeId: FitBaseType?
        get() = (getFieldValue(FIT_BASE_TYPE_ID_FIELD_NUM) as? UByte)?.let { FitBaseType.fromValue(it) }
        set(value) = setFieldValue(FIT_BASE_TYPE_ID_FIELD_NUM, value?.value)

    /** Number of values stored in field_name. */
    public val numFieldName: Int
        get() = getField(FIELD_NAME_FIELD_NUM)?.numValues ?: 0

    /** field_name. */
    public var fieldName: List<String?>?
        get() {
            val field = getField(FIELD_NAME_FIELD_NUM) ?: return null
            return List(field.numValues) { getFieldName(it) }
        }
        set(value) {
            removeField(FIELD_NAME_FIELD_NUM)
            value?.forEachIndexed { index, element -> setFieldName(index, element) }
        }

    public fun getFieldName(index: Int): String? =
        getFieldValue(FIELD_NAME_FIELD_NUM, index) as? String

    public fun setFieldName(index: Int, value: String?) {
        setFieldValue(FIELD_NAME_FIELD_NUM, value, index)
    }

    /** array. */
    public var array: UByte?
        get() = getFieldValue(ARRAY_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ARRAY_FIELD_NUM, value)

    /** components. */
    public var components: String?
        get() = getFieldValue(COMPONENTS_FIELD_NUM) as? String
        set(value) = setFieldValue(COMPONENTS_FIELD_NUM, value)

    /** scale. */
    public var scale: UByte?
        get() = getFieldValue(SCALE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(SCALE_FIELD_NUM, value)

    /** offset. */
    public var offset: Byte?
        get() = getFieldValue(OFFSET_FIELD_NUM) as? Byte
        set(value) = setFieldValue(OFFSET_FIELD_NUM, value)

    /** Number of values stored in units. */
    public val numUnits: Int
        get() = getField(UNITS_FIELD_NUM)?.numValues ?: 0

    /** units. */
    public var units: List<String?>?
        get() {
            val field = getField(UNITS_FIELD_NUM) ?: return null
            return List(field.numValues) { getUnits(it) }
        }
        set(value) {
            removeField(UNITS_FIELD_NUM)
            value?.forEachIndexed { index, element -> setUnits(index, element) }
        }

    public fun getUnits(index: Int): String? =
        getFieldValue(UNITS_FIELD_NUM, index) as? String

    public fun setUnits(index: Int, value: String?) {
        setFieldValue(UNITS_FIELD_NUM, value, index)
    }

    /** bits. */
    public var bits: String?
        get() = getFieldValue(BITS_FIELD_NUM) as? String
        set(value) = setFieldValue(BITS_FIELD_NUM, value)

    /** accumulate. */
    public var accumulate: String?
        get() = getFieldValue(ACCUMULATE_FIELD_NUM) as? String
        set(value) = setFieldValue(ACCUMULATE_FIELD_NUM, value)

    /** fit_base_unit_id. */
    public var fitBaseUnitId: FitBaseUnit?
        get() = (getFieldValue(FIT_BASE_UNIT_ID_FIELD_NUM) as? UShort)?.let { FitBaseUnit.fromValue(it) }
        set(value) = setFieldValue(FIT_BASE_UNIT_ID_FIELD_NUM, value?.value)

    /** native_mesg_num. */
    public var nativeMesgNum: MesgNum?
        get() = (getFieldValue(NATIVE_MESG_NUM_FIELD_NUM) as? UShort)?.let { MesgNum.fromValue(it) }
        set(value) = setFieldValue(NATIVE_MESG_NUM_FIELD_NUM, value?.value)

    /** native_field_num. */
    public var nativeFieldNum: UByte?
        get() = getFieldValue(NATIVE_FIELD_NUM_FIELD_NUM) as? UByte
        set(value) = setFieldValue(NATIVE_FIELD_NUM_FIELD_NUM, value)

    public companion object {
        public const val DEVELOPER_DATA_INDEX_FIELD_NUM: UByte = 0u
        public const val FIELD_DEFINITION_NUMBER_FIELD_NUM: UByte = 1u
        public const val FIT_BASE_TYPE_ID_FIELD_NUM: UByte = 2u
        public const val FIELD_NAME_FIELD_NUM: UByte = 3u
        public const val ARRAY_FIELD_NUM: UByte = 4u
        public const val COMPONENTS_FIELD_NUM: UByte = 5u
        public const val SCALE_FIELD_NUM: UByte = 6u
        public const val OFFSET_FIELD_NUM: UByte = 7u
        public const val UNITS_FIELD_NUM: UByte = 8u
        public const val BITS_FIELD_NUM: UByte = 9u
        public const val ACCUMULATE_FIELD_NUM: UByte = 10u
        public const val FIT_BASE_UNIT_ID_FIELD_NUM: UByte = 13u
        public const val NATIVE_MESG_NUM_FIELD_NUM: UByte = 14u
        public const val NATIVE_FIELD_NUM_FIELD_NUM: UByte = 15u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("DeveloperDataIndex", 0u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[1] = Field("FieldDefinitionNumber", 1u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[2] = Field("FitBaseTypeId", 2u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[3] = Field("FieldName", 3u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[4] = Field("Array", 4u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[5] = Field("Components", 5u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[6] = Field("Scale", 6u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[7] = Field("Offset", 7u, BaseType.SINT8, 1.0, 0.0, "", false)
            fields[8] = Field("Units", 8u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[9] = Field("Bits", 9u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[10] = Field("Accumulate", 10u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[13] = Field("FitBaseUnitId", 13u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[14] = Field("NativeMesgNum", 14u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[15] = Field("NativeFieldNum", 15u, BaseType.UINT8, 1.0, 0.0, "", false)
            return fields
        }
    }
}
